import type { QueryKey } from "@tanstack/react-query";
import type { AuthSessionChange } from "@buildpy/core";
import { createRpcClient, RpcError } from "@buildpy/api/client";
import { AUTH_UNAVAILABLE_CODE } from "@buildpy/core/lib/auth-unavailable";

import { navigateTo } from "@/lib/nav/router-ref";
import { invalidateActiveViews, noteActionSettled, queryClient, SIGNED_URL_KEY, VIEW_KEY } from "@/lib/query";
import { applySessionChange, readAccessToken, sessionRejected } from "@/lib/supabase";
import { clearOnboardingDrafts } from "@/modules/onboarding/draft-storage";

// 세션 갈아 끼우기가 끝난 뒤에 다음 요청이 토큰을 읽는다 — 로그인 직후 이동한 화면의 첫 읽기가 옛(빈) 세션으로 나가지 않게.
let sessionWork: Promise<void> = Promise.resolve();

// 화면에 붙은 읽기는 떼어질 때 버린다 — 붙은 채 지우면 다시 그릴 때 빈 세션으로 다시 읽어 401 이 난다(로그아웃 직후 셸).
const removeWhenDetached = (queryKey: QueryKey): void => {
  const cache = queryClient.getQueryCache();
  for (const query of cache.findAll({ queryKey, predicate: (q) => q.getObserversCount() > 0 })) {
    const stop = cache.subscribe((event) => {
      if (event.query !== query) return;
      if (event.type === "removed") stop();
      else if (event.type === "observerRemoved" && query.getObserversCount() === 0) {
        stop();
        cache.remove(query);
      }
    });
  }
  queryClient.removeQueries({ queryKey, predicate: (q) => q.getObserversCount() === 0 });
};

const forgetAccount = (change: AuthSessionChange): void => {
  // 계정이 바뀌면 이전 계정의 읽기 캐시를 버린다 — 로그아웃 뒤 다른 계정으로 들어와 옛 셸이 보이지 않게.
  const drop = change.kind === "clear" ? removeWhenDetached : (queryKey: QueryKey) => queryClient.removeQueries({ queryKey });
  drop([VIEW_KEY]);
  drop([SIGNED_URL_KEY]);
  // 세션이 걷히면(로그아웃 봉투·401 강제 로그아웃) 이 탭의 시공사 만들기 초안도 같이 지운다.
  if (change.kind === "clear") clearOnboardingDrafts();
};

const onSession = (change: AuthSessionChange): void => {
  forgetAccount(change);
  sessionWork = sessionWork
    .then(() => applySessionChange(change))
    .catch((error: unknown) => console.error("[session]", error));
};

// 서버가 401 을 준 세션을 이 브라우저에서 지운다 — 남겨 두면 로그인 화면이 그 세션으로 /app 에 되돌려 보낼 수 있다.
export const dropSession = async (): Promise<void> => {
  onSession({ kind: "clear" });
  await sessionWork;
};

const getAccessToken = async (): Promise<string | null> => {
  await sessionWork;
  return readAccessToken();
};

// 판정한 토큰이 아직 이 브라우저 세션일 때만 지운다 — 세션 작업 줄 안에서 비교해 그 사이 로그인한 새 세션을 지우지 않는다.
const dropSessionOf = async (token: string): Promise<void> => {
  sessionWork = sessionWork
    .then(async () => {
      if ((await readAccessToken()) !== token) return;
      forgetAccount({ kind: "clear" });
      await applySessionChange({ kind: "clear" });
    })
    .catch((error: unknown) => console.error("[session]", error));
  await sessionWork;
};

// 401 body 의 사유 — 서버 memberAuth 가 「토큰 무효」(unauthenticated) 와 「멤버 없음」(no_member) 을 가른다.
const unauthorizedReasonOf = (error: unknown): string | undefined => {
  if (!(error instanceof RpcError) || error.status !== 401) return undefined;
  const reason = (error.body as { error?: unknown } | null)?.error;
  return typeof reason === "string" ? reason : undefined;
};

// 토큰은 유효한데 멤버가 없는 401 — 세션을 남기고 시공사 만들기(/onboarding)로 보낼 신호.
export const isNoMemberError = (error: unknown): boolean => unauthorizedReasonOf(error) === "no_member";

// 인증 서버 장애 — api onError 가 AuthUnavailableError 를 503 body.error "auth_unavailable" 로 돌려준다(문구가 아니라 코드로 가른다).
export const isAuthUnavailableError = (error: unknown): boolean =>
  error instanceof RpcError && error.status === 503 && (error.body as { error?: unknown } | null)?.error === AUTH_UNAVAILABLE_CODE;

// 401 뒤 — unauthenticated 면 보낸 토큰의 세션을 바로 지우고 · no_member 면 남기고(온보딩) · 사유를 모르거나 보낸 토큰을 모르면 인증 서버에 물어 거절일 때만 지운다.
export const dropRejectedSession = async (error?: unknown): Promise<void> => {
  const reason = unauthorizedReasonOf(error);
  if (reason === "no_member") return;
  const sent = reason === "unauthenticated" ? (error as RpcError).token : null;
  if (sent) {
    await dropSessionOf(sent);
    return;
  }
  const token = await getAccessToken();
  if (!token) return;
  // 판정이 던지면 거절 아님 — 세션을 남기고 호출자(로그인 이동)를 막지 않는다.
  const rejected = await sessionRejected(token).catch((cause: unknown) => {
    console.error("[session]", cause);
    return false;
  });
  if (rejected) await dropSessionOf(token);
};

// 이 브라우저에 세션이 있는가 — 없으면 멤버 읽기를 보내지 않고 바로 로그인으로(401 콘솔 소음 막기).
export const hasSession = async (): Promise<boolean> => (await getAccessToken()) !== null;

// 액션(rpc POST) 응답마다 센다 — 봉투 invalidate 보다 먼저 세야 그 다시 읽기가 「이 액션 뒤」 로 잡힌다(refreshActiveViews).
const countingFetch = async (input: string, init?: RequestInit): Promise<Response> => {
  try {
    return await fetch(input, init);
  } finally {
    if (init?.method === "POST" && input.includes("/rpc/")) noteActionSettled();
  }
};

// 주소는 /api/... 를 그대로 쓴다 — 개발은 vite proxy(D6), 배포는 같은 출처.
export const api = createRpcClient({
  baseUrl: "",
  fetch: countingFetch,
  getAccessToken,
  onRedirect: (to) => navigateTo(to),
  onInvalidate: () => void invalidateActiveViews(),
  onSession,
});

export const { rpc, shareRpc, view, shareView, publicView, signUrls } = api;
